import React, { createContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import styled from 'styled-components';
import { api } from '../../services/api';
import Header from '../../components/Header';
import Container from '../../components/Container';
import { useAuth } from '../../hooks/useAuth';
import { UnderageUser } from '../../types/underageUser';
import DetailsUnderage from './components/DetailsUnderage';
import Confirm from './components/Confirm';
import Success from './components/Success';

import { Loading } from '../../components/Loading';
import { Actions, Categories } from '../../types/gtm';
import SubmitConfirm from './components/SubmitConfirm';

export const LetterAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 16px;
  background-color: #0069de;
  color: #ffffff;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const DivFooter = styled.div`
  padding-bottom: 24px;
  display: flex;
  justify-content: center;

  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: 650px) {
    > button {
      width: 100%;
    }
  }
`;

export type Step = {
  screen: string;
  type?: string;
};

export type ParentalConsentContextProps = {
  underageUser: UnderageUser;
  step: Step;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  dateToBlock: string;
  setDateToBlock: React.Dispatch<React.SetStateAction<string>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const defaultValues = {
  underageUser: {
    underageId: '',
    canBeManaged: false,
    email: '',
    name: '',
    status: 0,
    authorizationAt: '',
    blockedAt: '',
    blocked_until: '',
    approvalDeadline: '',
  },
  step: { screen: '', type: '' },
  setStep: () => ({}),
  dateToBlock: '',
  setDateToBlock: () => ({}),
  setLoading: () => ({}),
};

export const ParentalConsentContext =
  createContext<ParentalConsentContextProps>(defaultValues);

const ParentalConsentPage = () => {
  const { underageId, step: routeStep } =
    useParams<{ underageId: string; step: string }>();
  const { user, logout, authenticateOnAPI } = useAuth();

  const [underageUser, setUnderageUser] = useState<UnderageUser>(
    defaultValues.underageUser,
  );
  const [step, setStep] = useState<Step>({
    screen: '',
    type: '',
  });
  const [dateToBlock, setDateToBlock] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const getUnderageUser = async () => {
      setLoading(true);
      const token = await authenticateOnAPI();
      api
        .get(`/parental-consent/${underageId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<UnderageUser>) => {
          if (isMounted) {
            setLoading(false);
            setUnderageUser(response.data);
            setStep({ screen: routeStep || 'details', type: step.type });
          }
        })
        .catch(() => {
          setLoading(false);
          toast.error('Erro ao buscar informações do menor de idade');
          setStep({ screen: 'error', type: step.type });
        });
    };

    getUnderageUser();

    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.ParentalConsent,
      event_action: Actions.Pageview,
      bd_suser_code: user?.globoId,
    });

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <ParentalConsentContext.Provider
      value={{
        underageUser,
        step,
        setStep,
        dateToBlock,
        setDateToBlock,
        setLoading,
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="colored"
      />
      <Header
        user={user}
        onLogout={logout}
        showReturn
        screenMenu={Categories.ParentalConsent}
        onReturn={({ handleSmartReturn }) => {
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.ParentalConsent,
            event_action: Actions.ClickBack,
            bd_suser_code: user?.globoId,
          });
          if (['', 'error'].includes(step.screen)) {
            handleSmartReturn();
          } else {
            setStep({ screen: '', type: '' });
          }
        }}
      />

      <Container errorInRequest={step.screen === 'error'}>
        {(() => {
          switch (step.screen) {
            case 'confirm':
              return <Confirm />;
            case 'submitConfirm':
              return <SubmitConfirm />;
            case 'success':
              return <Success />;
            default:
              return <DetailsUnderage />;
          }
        })()}
      </Container>
    </ParentalConsentContext.Provider>
  );
};

export default ParentalConsentPage;
